import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  loadImporterReportPreview,
  mintImporterReportDownload,
} from '../../../utils/api';
import { triggerPdfDownload } from '../../../utils/triggerPdfDownload';
import { snapshotLoadingText } from '../../../utils/seasonLoadingText';

/**
 * PLAN.md View-all popup: published next10 sidecar rows + daily PDF download.
 * Does not render live sea_tracking top_importers (those stay on the scorecards).
 */
const TopImportersModal = ({
  isOpen,
  onClose,
  selectedCommodity = '',
}) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [loadState, setLoadState] = useState('idle'); // idle|loading|ready|missing|error
  const [loadErr, setLoadErr] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState('');

  const commodity = String(selectedCommodity || '').trim().toLowerCase();
  const dailySupported = commodity === 'grapes';
  const generatedAt = preview?.generated_at || '';
  const rows = Array.isArray(preview?.rows) ? preview.rows : [];
  const canDailyDownload = Boolean(generatedAt) && dailySupported;

  useEffect(() => {
    if (!isOpen) {
      setPreview(null);
      setLoadState('idle');
      setLoadErr('');
      setPdfErr('');
      setPdfBusy(false);
      return undefined;
    }
    if (!dailySupported) {
      setPreview(null);
      setLoadState('missing');
      setLoadErr('');
      return undefined;
    }

    let cancelled = false;
    setLoadState('loading');
    setLoadErr('');
    loadImporterReportPreview({
      destination: 'USA',
      commodity: 'grapes',
      kind: 'next10',
    })
      .then((body) => {
        if (cancelled) return;
        const payload = body?.preview && typeof body.preview === 'object' ? body.preview : body;
        const at = body?.generated_at || payload?.generated_at || '';
        const list = Array.isArray(payload?.rows) ? payload.rows : [];
        setPreview({ generated_at: at, rows: list, ...payload });
        setLoadState(at ? 'ready' : 'missing');
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.message || '';
        if (/not available|404/i.test(msg)) {
          setLoadState('missing');
          setLoadErr('');
        } else {
          setLoadState('error');
          setLoadErr(msg || t('map.scorecards.previewError', 'Could not load snapshot'));
        }
        setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, dailySupported, t]);

  if (!isOpen) return null;

  const onDownloadDaily = async () => {
    if (pdfBusy || !canDailyDownload) return;
    setPdfBusy(true);
    setPdfErr('');
    try {
      const body = await mintImporterReportDownload({
        destination: 'USA',
        commodity: 'grapes',
        kind: 'next10',
      });
      triggerPdfDownload(body.url);
    } catch (e) {
      setPdfErr(e?.message || t('map.scorecards.pdfError', 'Report unavailable'));
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2200] flex items-center justify-center bg-black/40 p-4 max-lg:pb-[max(1rem,env(safe-area-inset-bottom))]"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="top-importers-modal-title"
        className="flex max-h-[85dvh] w-full max-w-3xl flex-col overflow-hidden rounded-xl max-lg:self-end max-lg:rounded-b-none max-lg:rounded-t-2xl bg-white shadow-xl dark:bg-[#1d1d1d]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#E8EBE9] px-5 py-4 dark:border-[#30363d]">
          <div>
            <h2
              id="top-importers-modal-title"
              className="font-['Inter'] text-[16px] font-semibold text-[#282A29] dark:text-[#f0f5f2]"
            >
              {t('map.scorecards.viewAllTitle', 'Top importers — daily snapshot')}
            </h2>
            <p className="mt-1 font-['Inter'] text-[11px] text-[#828382]">
              {dailySupported
                ? t(
                    'map.scorecards.pdfSnapshotLabel',
                    'Daily multi-mode snapshot · USA · grapes · next 10 days',
                  )
                : t(
                    'map.scorecards.dailyNotForCommodity',
                    'Daily next-10 snapshot is published for USA grapes only. Use Download season PDF for this commodity.',
                  )}
              {generatedAt ? (
                <span className="block mt-0.5">
                  {t('map.scorecards.generatedAt', 'Generated {{when}}', { when: generatedAt })}
                </span>
              ) : null}
            </p>
            <p className="mt-2 font-['Inter'] text-[11px] text-[#5d625f] dark:text-[#a8b0ab]">
              {t(
                'map.scorecards.dailyNotLiveFilters',
                'This PDF is the published daily report. It does not follow the map’s importer, exporter, origin, status, or mode filters.',
              )}{' '}
              {t(
                'map.scorecards.dailyThinV1',
                'v1 is one importer table (not the full sample KPI / detail layout).',
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-m-1 inline-flex items-center justify-center rounded-md p-1 text-[#828382] hover:bg-[#F0F5F2] max-lg:min-h-11 max-lg:min-w-11 lg:m-0 dark:hover:bg-[#30363d]"
            aria-label={t('common.close', 'Close')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-5 py-3">
          {loadState === 'loading' ? (
            <p className="font-['Inter'] text-[13px] text-[#828382]">
              {snapshotLoadingText(t)}
            </p>
          ) : null}
          {loadState === 'error' ? (
            <p className="font-['Inter'] text-[13px] text-[#EF4444]">{loadErr}</p>
          ) : null}
          {loadState === 'missing' ? (
            <p className="font-['Inter'] text-[13px] text-[#828382]">
              {dailySupported
                ? t('map.scorecards.snapshotNotPublished', 'Snapshot not published yet.')
                : t(
                    'map.scorecards.dailyNotForCommodity',
                    'Daily next-10 snapshot is published for USA grapes only. Use Download season PDF for this commodity.',
                  )}
            </p>
          ) : null}
          {loadState === 'ready' ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] border-collapse font-['Inter'] text-[12px]">
                <thead>
                  <tr className="border-b border-[#E8EBE9] text-left text-[#828382] dark:border-[#30363d]">
                    <th className="py-2 pr-2 font-medium">{t('map.scorecards.colImporter', 'Importer')}</th>
                    <th className="py-2 pr-2 font-medium">{t('map.scorecards.colOrigin', 'Origin')}</th>
                    <th className="py-2 pr-2 font-medium">{t('map.scorecards.colMode', 'Mode')}</th>
                    <th className="py-2 pr-2 font-medium text-right">{t('map.scorecards.colContainers', 'Containers')}</th>
                    <th className="py-2 pr-2 font-medium text-right">{t('map.scorecards.colTonnes', 'Tonnes')}</th>
                    <th className="py-2 font-medium text-right">{t('map.scorecards.colBoxes', '8.2 kg boxes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-3 text-[#828382]">
                        {t('map.scorecards.noSnapshotRows', 'No shipments in window')}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, i) => (
                      <tr
                        key={`${r.importer || ''}-${r.origin || ''}-${r.mode || ''}-${i}`}
                        className="border-b border-[#F0F5F2] dark:border-[#30363d]"
                      >
                        <td className="py-2 pr-2 text-[#282A29] dark:text-[#f0f5f2]">{r.importer || '—'}</td>
                        <td className="py-2 pr-2 text-[#5d625f]">{r.origin || '—'}</td>
                        <td className="py-2 pr-2 text-[#5d625f]">{r.mode || '—'}</td>
                        <td className="py-2 pr-2 text-right tabular-nums">{Number(r.containers || 0).toLocaleString()}</td>
                        <td className="py-2 pr-2 text-right tabular-nums">{Number(r.tonnes || 0).toLocaleString()}</td>
                        <td className="py-2 text-right tabular-nums">{Number(r.boxes || 0).toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 border-t border-[#E8EBE9] px-5 py-3 dark:border-[#30363d]">
          {pdfErr ? (
            <div className="font-['Inter'] text-[11px] text-[#EF4444]">{pdfErr}</div>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={onDownloadDaily}
              disabled={!canDailyDownload || pdfBusy}
              className="inline-flex min-h-11 flex-1 items-center justify-center gap-1 rounded-[6px] border border-[#E8EBE9] bg-white px-3 py-2 font-['Inter'] text-[12px] font-semibold text-[#282A29] hover:border-bloom-green/60 disabled:opacity-50 lg:min-h-0 lg:flex-none dark:border-[#30363d] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]"
            >
              <Download size={14} className="text-bloom-green" />
              {pdfBusy
                ? t('map.scorecards.pdfBusy', 'Downloading…')
                : t('map.scorecards.downloadDailySnapshot', 'Download daily multi-mode PDF')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="min-h-11 flex-1 rounded-lg bg-bloom-green px-4 py-2 font-['Inter'] text-[12px] font-medium text-white hover:bg-bloom-green-hover lg:min-h-0 lg:flex-none"
            >
              {t('common.done', 'Done')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopImportersModal;
